import React from 'react'
import moment from 'moment-timezone'
import type { AuctionBlueprintScheduledOnlineReactiveArgs } from '@adc/blueprint'
import _cx from 'classnames/bind'
import {
  hooks,
  Text,
  utils,
  Badge,
  PopupTooltip,
  Icon,
  constants,
} from '@adc/ui-adc'
import * as graph from '@adc/sdk'
import analytics from '../../services/analytics'
import cls from './styles.scss'
const cx = _cx.bind(cls)

const MINUTE = 60
const HOUR = MINUTE * 60
const AUCTION_COUNTDOWN_URGENT_THRESHOLD = 4 * HOUR // 4 hours
const isUrgent = (secondsLeft: number) =>
  secondsLeft < AUCTION_COUNTDOWN_URGENT_THRESHOLD

// these functions are exported to be used by the functions using these countdown components
// before they actually render the components in order to consolidate which fields of segment we want to use for timers
export const getStartTimeMoment = (
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
) => moment(segment.start_date, constants.API_FULL_DATETIME_FORMAT)
export const getEndTimeMoment = (
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
) => moment(segment.initial_end_date, constants.API_FULL_DATETIME_FORMAT)
export const getServerTimeMoment = (
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
) => moment(segment.current_time, constants.API_FULL_DATETIME_FORMAT)

export const formatStartDateTime = (secondsRemaining: number) =>
  secondsRemaining > 0
    ? utils.countdown.formatCountdownTimeWithoutText(secondsRemaining)
    : 'a moment'

interface ViewProps {
  label?: string
  value: string
  color?: string
  weight?: 'normal' | 'medium' | 'heavy' | 'black' | undefined
  className?: string
}

interface CountdownDefaultProps {
  currentServerTime: moment.Moment
  endTime: moment.Moment
}

const View = ({
  label,
  value,
  color = '',
  weight = undefined,
  className = '',
  ...rest
}: ViewProps) => (
  <Text
    data-elm-id='auction-countdown'
    color={color}
    weight={weight}
    className={className}
    {...rest}
  >
    {label && <span data-elm-id='label'>{label}</span>}
    {label && <span>&nbsp;</span>}
    <span data-elm-id='value'>{value}</span>
  </Text>
)

interface StartingProps {
  endTime: moment.Moment
  currentServerTime: moment.Moment
  className?: string
}

export const Starting = ({
  endTime, // this is actually the bidding start time for this component but the useCountdown hook doesn't expects endTime so we don't rename it just for this component
  currentServerTime,
  className = '',
  ...rest // TODO: make these explicit
}: StartingProps) => {
  // @ts-ignore useCountdown hook should be better typed
  const { secondsRemaining } = hooks.useCountdown({
    // @ts-ignore useCountdown hook should be better typed
    currentServerTime,
    endTime,
    updateInterval: 250,
  })
  return (
    <View
      color='white'
      value={formatStartDateTime(secondsRemaining)}
      weight='heavy'
      className={className}
      {...rest}
    />
  )
}

export const CountdownBefore = ({
  endTime,
  currentServerTime,
}: StartingProps) => (
  <div className={cx('container')} data-elm-id='countdown_container'>
    <Text data-elm-id='countdown_label'>Auction Starts In:</Text>
    <div className={cx('countdown-container', 'u-ml-2')}>
      <Starting endTime={endTime} currentServerTime={currentServerTime} />
    </div>
  </div>
)

export const Ending = ({
  currentServerTime,
  endTime,
}: CountdownDefaultProps) => {
  // @ts-ignore useCountdown hook should be better typed
  const { secondsRemaining } = hooks.useCountdown({
    // @ts-ignore useCountdown hook should be better typed
    currentServerTime,
    endTime,
    updateInterval: 250,
  })
  return (
    <View
      color={isUrgent(secondsRemaining) ? 'error' : 'gray'}
      value={utils.countdown.formatCountdownTimeWithText(secondsRemaining)}
      weight='heavy'
    />
  )
}

interface CountdownDuringProps extends CountdownDefaultProps {
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
}

export const CountdownDuring = ({
  currentServerTime,
  endTime,
  segment,
}: CountdownDuringProps) => {
  // @ts-ignore useCountdown hook should be better typed
  const { secondsRemaining, shouldFlash } = hooks.useCountdown({
    // @ts-ignore useCountdown hook should be better typed
    currentServerTime,
    endTime,
    updateInterval: 250,
  })

  const { primary, secondary = '' } =
    segment.__methods__.formatCountdownTime(secondsRemaining) || {}

  // if we get a secondary text back from the countdown time formatter, then it should override any other state such as extended
  const showAuctionExtended =
    segment?.current_bid_extension?.reason ===
      graph.enums.OnlineSegmentExtensionReason.PendingSellerConfirmation &&
    !secondary

  const trackExtensionPopupOpened = () => {
    analytics.popup('bidding_extended_tooltip', {
      event: 'hover-event',
      tooltipName: 'bidding_extended_tooltip',
    })
  }

  const secondaryContent =
    secondary ||
    (showAuctionExtended ? (
      <>
        EXTENDED{' '}
        <PopupTooltip
          content='This auction has been extended. Bidding was near but did not meet the reserve price, so we extended the auction to give everyone a chance to place higher bids.'
          elmId='extended_popup_tooltip'
          trigger={
            <Icon
              name='info circle'
              data-elm-id='extended_popup_tooltip_trigger'
            />
          }
          onOpen={trackExtensionPopupOpened}
          offset={undefined}
          horizontalOffset={undefined}
          verticalOffset={undefined}
        />
      </>
    ) : (
      ''
    ))

  let badgeMode = 'default'
  if (showAuctionExtended && !shouldFlash) {
    badgeMode = 'info'
  }

  return (
    <div className={cx('container')} data-elm-id='countdown_container'>
      <Text data-elm-id='countdown_label'>Bidding Ends In:</Text>
      <Badge
        elmId='auction-countdown'
        primary={primary}
        secondary={secondaryContent}
        flashing={Boolean(shouldFlash)}
        // @ts-ignore export BadgeMode from DS to cast this var as
        mode={badgeMode}
      />
    </div>
  )
}
